import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  CloudUpload,
  Delete,
  Visibility,
  CheckCircle,
  Cancel
} from '@mui/icons-material';
import { authGet, authPost, authPut, authDelete, authPostFormData } from '../../services/apiService';

/**
 * PromptUploadTab component for uploading and managing test plan prompts
 * Requires authentication (admin only)
 */
const PromptUploadTab = () => {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [prompts, setPrompts] = useState([]);
  const [activePrompt, setActivePrompt] = useState(null);
  
  // Upload form state
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [promptIdentifier, setPromptIdentifier] = useState('');
  const [promptCategory, setPromptCategory] = useState('test-plan');
  const [promptDescription, setPromptDescription] = useState('');
  
  // View dialog state
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewingPrompt, setViewingPrompt] = useState(null);
  
  // Activating state
  const [activating, setActivating] = useState({});

  useEffect(() => {
    fetchPrompts();
  }, []);

  // Auto-clear messages
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError('');
        setSuccess('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  const fetchPrompts = async () => {
    try {
      setLoading(true);
      setError('');

      const [activeResponse, historyResponse] = await Promise.all([
        authGet('/api/admin/prompts/active?category=test-plan'),
        authGet('/api/admin/prompts/history?category=test-plan&limit=50')
      ]);

      if (activeResponse.success) {
        setActivePrompt(activeResponse.data);
      }

      if (historyResponse.success) {
        setPrompts(historyResponse.data || []);
      }
    } catch (err) {
      console.error('Error fetching prompts:', err);
      setError('Failed to fetch prompts: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.md')) {
        setError('Please select a .md file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        return;
      }
      setSelectedFile(file);
      setError('');
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file');
      return;
    }
    if (!promptIdentifier.trim()) {
      setError('Please enter a prompt identifier');
      return;
    }

    try {
      setUploading(true);
      setError('');

      // Create form data
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('name', promptIdentifier);
      formData.append('category', promptCategory);
      formData.append('description', promptDescription);

      const response = await authPostFormData('/api/admin/prompts/upload', formData);

      if (response.success) {
        setSuccess('Prompt uploaded successfully!');
        setUploadDialogOpen(false);
        resetUploadForm();
        fetchPrompts();
      } else {
        setError(response.message || 'Failed to upload prompt');
      }
    } catch (err) {
      console.error('Error uploading prompt:', err);
      setError('Failed to upload prompt: ' + err.message);
    } finally {
      setUploading(false);
    }
  };


  const handleActivate = async (promptId) => {
    try {
      setActivating(prev => ({ ...prev, [promptId]: true }));
      setError('');

      const response = await authPut(`/api/admin/prompts/${promptId}/activate`, {
        category: 'test-plan'
      });

      if (response.success) {
        setSuccess('Prompt activated successfully!');
        fetchPrompts();
      } else {
        setError(response.message || 'Failed to activate prompt');
      }
    } catch (err) {
      console.error('Error activating prompt:', err);
      setError('Failed to activate prompt: ' + err.message);
    } finally {
      setActivating(prev => ({ ...prev, [promptId]: false }));
    }
  };

  const handleDelete = async (promptId) => {
    if (!window.confirm('Are you sure you want to delete this prompt? This action cannot be undone.')) {
      return;
    }

    try {
      setError('');

      const response = await authDelete(`/api/admin/prompts/${promptId}`);

      if (response.success) {
        setSuccess('Prompt deleted successfully!');
        fetchPrompts();
      } else {
        setError(response.message || 'Failed to delete prompt');
      }
    } catch (err) {
      console.error('Error deleting prompt:', err);
      setError('Failed to delete prompt: ' + err.message);
    }
  };

  const handleView = async (prompt) => {
    try {
      setLoading(true);
      // Fetch full prompt with content
      const response = await authGet(`/api/admin/prompts/${prompt._id}`);
      if (response.success) {
        setViewingPrompt(response.data);
        setViewDialogOpen(true);
      } else {
        setError('Failed to fetch prompt details');
      }
    } catch (err) {
      console.error('Error fetching prompt:', err);
      setError('Failed to fetch prompt details: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetUploadForm = () => {
    setSelectedFile(null);
    setPromptIdentifier('');
    setPromptCategory('test-plan');
    setPromptDescription('');
    // Reset file input
    const fileInput = document.getElementById('prompt-file-input');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>Loading prompts...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Error/Success Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" gutterBottom>
            Prompt Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Upload and manage test plan generation prompts (requires authentication)
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<CloudUpload />}
          onClick={() => setUploadDialogOpen(true)}
        >
          Upload Prompt
        </Button>
      </Box>

      {/* Active Prompt Card */}
      {activePrompt && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'success.light', color: 'success.contrastText' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6" gutterBottom>
                Active Prompt: {activePrompt.name}
              </Typography>
              <Typography variant="body2">
                Version {activePrompt.version} • Last updated: {formatDate(activePrompt.updatedAt)}
              </Typography>
              {activePrompt.description && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {activePrompt.description}
                </Typography>
              )}
            </Box>
            <Chip label="Active" color="success" />
          </Box>
        </Paper>
      )}

      {/* Prompts Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Identifier/Name</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Version</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Updated By</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {prompts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    No prompts found. Upload your first prompt to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              prompts.map((prompt) => (
                <TableRow key={prompt._id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={prompt.active ? 'bold' : 'normal'}>
                      {prompt.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={prompt.category || 'test-plan'} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={prompt.active ? 'bold' : 'normal'}>
                      {prompt.version}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                      {prompt.description || 'No description'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {prompt.active ? (
                      <Chip label="Active" color="success" size="small" icon={<CheckCircle />} />
                    ) : (
                      <Chip label="Inactive" color="default" size="small" icon={<Cancel />} />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(prompt.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {prompt.updatedBy?.displayName || prompt.updatedBy?.username || 'System'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      <Tooltip title="View prompt content">
                        <IconButton
                          size="small"
                          onClick={() => handleView(prompt)}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      {!prompt.active && (
                        <Tooltip title="Activate prompt">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleActivate(prompt._id)}
                            disabled={activating[prompt._id]}
                          >
                            {activating[prompt._id] ? (
                              <CircularProgress size={16} />
                            ) : (
                              <CheckCircle />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}
                      {!prompt.active && (
                        <Tooltip title="Delete prompt">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(prompt._id)}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Upload Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => {
          setUploadDialogOpen(false);
          resetUploadForm();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Upload New Prompt</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Prompt Identifier"
              value={promptIdentifier}
              onChange={(e) => setPromptIdentifier(e.target.value)}
              fullWidth
              required
              helperText="A unique name/ID for this prompt (e.g., 'v1.2', 'security-focused', etc.)"
            />
            
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={promptCategory}
                onChange={(e) => setPromptCategory(e.target.value)}
                label="Category"
              >
                <MenuItem value="test-plan">Test Plan</MenuItem>
                <MenuItem value="security-test">Security Test</MenuItem>
                <MenuItem value="regression-test">Regression Test</MenuItem>
                <MenuItem value="integration-test">Integration Test</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Description (Optional)"
              value={promptDescription}
              onChange={(e) => setPromptDescription(e.target.value)}
              fullWidth
              multiline
              rows={2}
              helperText="Brief description of what this prompt is used for"
            />

            <Box>
              <input
                accept=".md"
                style={{ display: 'none' }}
                id="prompt-file-input"
                type="file"
                onChange={handleFileSelect}
              />
              <label htmlFor="prompt-file-input">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUpload />}
                  fullWidth
                >
                  {selectedFile ? selectedFile.name : 'Select .md File'}
                </Button>
              </label>
              {selectedFile && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  File: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setUploadDialogOpen(false);
              resetUploadForm();
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={uploading || !selectedFile || !promptIdentifier.trim()}
            startIcon={uploading ? <CircularProgress size={16} /> : <CloudUpload />}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {viewingPrompt?.name} (Version {viewingPrompt?.version})
        </DialogTitle>
        <DialogContent>
          {viewingPrompt && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Description:
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                {viewingPrompt.description || 'No description'}
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                Content:
              </Typography>
              <TextField
                multiline
                rows={15}
                value={viewingPrompt.content || ''}
                variant="outlined"
                fullWidth
                InputProps={{
                  readOnly: true,
                  style: { fontFamily: 'monospace', fontSize: '12px' }
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PromptUploadTab;

